/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    bool, string, func,
    object, shape, oneOfType,
} from 'prop-types';
import { connect } from 'react-redux';
import ReactHTMLParser from 'react-html-parser';

import withStyles from '@material-ui/core/styles/withStyles';
import Modal from '@material-ui/core/Modal';
import Close from '@material-ui/icons/Close';
import { combineStyle } from '../style/combineStyle';
import { main as MainStyle } from '../style/main';
import { form as FormStyle } from '../style/form';
import { innerModal as InnerModalStyle } from '../style/innerModal';

import { deepObjectExtend } from '../utils/object';
import getSiteConfig from '../utils/getSiteConfig';

import AnimatedCheck from '../assets/svg/AnimatedCheck';
import RemoveRecipient from '../components/ModalContent/RemoveRecipient';
import RemoveCreditCard from '../components/ModalContent/RemoveCreditCard';
import AddressForm from './ModalContent/AddressForm';
import CreditCardForm from './ModalContent/CreditCardForm';
import HTMLContent from './ModalContent/HTMLContent';
import ChangePasswordForm from './ModalContent/Desktop/ChangePasswordForm';
import ChangeEmailForm from './ModalContent/Desktop/ChangeEmailForm';
import DesktopAddressForm from './ModalContent/Desktop/DesktopAddressForm';
import DesktopAddressRemove from '../components/AddressBook/DesktopAddressBook/DesktopAddressRemove';
import DesktopRemoveCreditCard from '../components/BillingAddress/DesktopBillingAddress/DesktopRemoveCreditCard';
import DesktopCreditCardAndBillingForm from './ModalContent/DesktopCreditCardAndBillingForm';

import EventDispatcher from '../state/reducers/EventDispatcher';
import configEnv from '../config';

const { PERSIST_KEY } = configEnv;

const styles = ({ typography, palette }) => deepObjectExtend(
    combineStyle(typography, palette, {
        MainStyle,
        InnerModalStyle,
        FormStyle,
    }),
    {
        main: {
            width: '100vw',
            margin: '0',

            '&.waiting': {
                '& .UIModal-innerModal > *': {
                    filter: 'grayscale(1)',
                    opacity: '0.6',
                },
                '&:before': {
                    display: 'block',
                    content: "''",
                    // 'background-color': 'rgb(255,255,255,0.3)',
                    top: '0',
                    left: '0',
                    right: '0',
                    bottom: '0',
                    'z-index': 2,
                    position: 'fixed',
                    'touch-action': 'none',
                },
            },
            '& > .backdrop': {
                'background-color': 'rgba(0, 0, 0, 0.9) !important',
            },
        },
    },
);

const Components = {
    RemoveRecipient,
    RemoveCreditCard,
    AddressForm,
    CreditCardForm,
    HTMLContent,
    ChangePasswordForm,
    ChangeEmailForm,
    DesktopAddressForm,
    DesktopAddressRemove,
    DesktopRemoveCreditCard,
    DesktopCreditCardAndBillingForm,
};

const UIModal = (props) => {
    const {
        classes, isReadyState, brandId,
        isOpen, addToCart, modal, handleCloseModal,
    } = props;

    /* istanbul ignore next */
    if (!modal) return null;

    /* eslint object-curly-newline: "off" */
    const { title, customClass, layout, config: modalConfig, isMainModal } = modal;
    const { content } = modal;

    // if (title === 'Your Privacy') content = <div>Your Privacy</div>;
    /* istanbul ignore next */
    if (!layout) return null;
    let Layout = <div>Empty modal</div>;
    const Component = Components[layout];
    if (Component) {
        Layout = layout !== 'dialog' ? Component : React.createElement('div');
    } else {
        console.error(`UIModal&#39; layout was not '${layout}' not found in ${Object.keys(Components)}`);
        return null;
    }
    // console.log('UIModal - content', content);
    return (
        <Modal
            open={isOpen}
            onClose={() => handleCloseModal(isMainModal)}
            className={`${classes.main} ${!isReadyState ? 'waiting' : ''}`}
            closeAfterTransition
            BackdropProps={
                {
                    classes: {
                        root: 'backdrop',
                    },
                }
            }
            disableBackdropClick
            disableEscapeKeyDown
        >
            <div className={`${classes.innerModal} UIModal-innerModal ${brandId} ${customClass}`}>
                <div
                    className={
                        title === '' ? 'modal-title empty-title' : 'modal-title'
                    }
                >
                    {addToCart && layout === 'RecipientDetails' ? (
                        <AnimatedCheck className="success-color margin-right-half" />
                    ) : (
                        ''
                    )}
                    <h2 className="no-margin">{title}</h2>
                    {layout !== 'dialog' && modalConfig !== 'ErrorOutline' ? (
                        <Close className="x-icon" onClick={() => handleCloseModal(isMainModal)} />
                    ) : (
                        ''
                    )}
                </div>
                <div className={`modal-content-wrapper ${layout}`}>
                    {layout && layout !== 'dialog' ? (
                        <Layout
                            isInsideModal="true"
                            content={content}
                            config={modalConfig}
                        />
                    ) : (
                        ReactHTMLParser(content)
                    )}
                </div>
            </div>
        </Modal>
    );
};

UIModal.defaultProps = {
    addToCart: '',
    isReadyState: false,
    isOpen: false,
    modal: {},
    handleCloseModal: () => { },
};

UIModal.propTypes = {
    classes: object.isRequired,
    isReadyState: bool,
    isOpen: bool,
    addToCart: string,
    modal: shape({
        open: bool,
        title: string,
        content: oneOfType([
            object,
            string,
        ]),
        customClass: string,
        layout: string,
        config: string,
    }),
    handleCloseModal: func,
    brandId: string.isRequired,
};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    // console.log('UIModal - newState', newState);
    const brandId = getSiteConfig('brand-id');
    return {
        isReadyState: newState.isReadyState,
        modal: newState.modal,
        addToCart:
            typeof newState.modal.content === 'object'
                ? newState.modal.content.addToCart
                : '',
        title: newState.modal.title, // use this to trigger modal
        content: newState.modal.content, // use this to trigger modal
        isOpen: newState.modal.open, // use this to trigger modal
        brandId,
    };
};

const mapDispatchToProps = (dispatch) => ({
    handleCloseModal: (isMainModal) => dispatch(EventDispatcher.handleCloseModal(isMainModal)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles)(UIModal));
